// Footer Manager Component
import React, { useState, useEffect } from 'react';
import { Loader2, Save, MapPin, Phone, Mail } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FooterManager = () => {
  const [settings, setSettings] = useState({
    description_en: "", description_fr: "", description_ar: "",
    address: "", phone: "", email: "",
    hours_weekdays_en: "", hours_weekdays_fr: "", hours_weekdays_ar: "", hours_weekdays_time: "",
    hours_weekend_en: "", hours_weekend_fr: "", hours_weekend_ar: "",
    hours_weekend_status_en: "", hours_weekend_status_fr: "", hours_weekend_status_ar: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/footer-settings`, { credentials: 'include' });
      if (response.ok) { const data = await response.json(); setSettings(data); }
    } catch (err) { console.error('Error fetching footer settings:', err); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/footer-settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(settings)
      });
      if (response.ok) { alert('Paramètres du pied de page sauvegardés !'); }
    } catch (err) { console.error('Error saving footer settings:', err); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-light text-slate-800">Pied de Page</h2>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sauvegarde...</> : <><Save className="w-4 h-4 mr-2" /> Sauvegarder</>}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Description */}
        <div className="border border-slate-200 rounded-lg p-4">
          <h3 className="font-medium text-slate-800 mb-4">Description de l'entreprise</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">🇫🇷 Description FR</label>
              <textarea value={settings.description_fr} onChange={(e) => setSettings({...settings, description_fr: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" rows={3} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">🇬🇧 Description EN</label>
              <textarea value={settings.description_en} onChange={(e) => setSettings({...settings, description_en: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" rows={3} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">🇩🇿 الوصف AR</label>
              <textarea value={settings.description_ar || ''} onChange={(e) => setSettings({...settings, description_ar: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-right" dir="rtl" rows={3} />
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="border border-slate-200 rounded-lg p-4">
          <h3 className="font-medium text-slate-800 mb-4">Informations de contact</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1"><MapPin className="w-4 h-4 inline mr-1" /> Adresse</label>
              <input type="text" value={settings.address} onChange={(e) => setSettings({...settings, address: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1"><Phone className="w-4 h-4 inline mr-1" /> Téléphone</label>
              <input type="text" value={settings.phone} onChange={(e) => setSettings({...settings, phone: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1"><Mail className="w-4 h-4 inline mr-1" /> Email</label>
              <input type="text" value={settings.email} onChange={(e) => setSettings({...settings, email: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
          </div>
        </div>

        {/* Hours */}
        <div className="border border-slate-200 rounded-lg p-4">
          <h3 className="font-medium text-slate-800 mb-4">Horaires d'ouverture</h3>
          <div className="mb-4">
            <p className="text-sm text-slate-500 mb-2">Jours de semaine</p>
            <div className="grid grid-cols-4 gap-4">
              <div><label className="block text-xs text-slate-600 mb-1">🇫🇷 Jours FR</label><input type="text" value={settings.hours_weekdays_fr} onChange={(e) => setSettings({...settings, hours_weekdays_fr: e.target.value})} placeholder="Dim - Jeu" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
              <div><label className="block text-xs text-slate-600 mb-1">🇬🇧 Jours EN</label><input type="text" value={settings.hours_weekdays_en} onChange={(e) => setSettings({...settings, hours_weekdays_en: e.target.value})} placeholder="Sun - Thu" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
              <div><label className="block text-xs text-slate-600 mb-1">🇩🇿 الأيام AR</label><input type="text" value={settings.hours_weekdays_ar || ''} onChange={(e) => setSettings({...settings, hours_weekdays_ar: e.target.value})} placeholder="الأحد - الخميس" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-right" dir="rtl" /></div>
              <div><label className="block text-xs text-slate-600 mb-1">Horaires</label><input type="text" value={settings.hours_weekdays_time} onChange={(e) => setSettings({...settings, hours_weekdays_time: e.target.value})} placeholder="8:00 - 17:00" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
            </div>
          </div>
          <div>
            <p className="text-sm text-slate-500 mb-2">Week-end</p>
            <div className="grid grid-cols-3 gap-4 mb-3">
              <div><label className="block text-xs text-slate-600 mb-1">🇫🇷 Jours FR</label><input type="text" value={settings.hours_weekend_fr} onChange={(e) => setSettings({...settings, hours_weekend_fr: e.target.value})} placeholder="Ven - Sam" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
              <div><label className="block text-xs text-slate-600 mb-1">🇬🇧 Jours EN</label><input type="text" value={settings.hours_weekend_en} onChange={(e) => setSettings({...settings, hours_weekend_en: e.target.value})} placeholder="Fri - Sat" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
              <div><label className="block text-xs text-slate-600 mb-1">🇩🇿 الأيام AR</label><input type="text" value={settings.hours_weekend_ar || ''} onChange={(e) => setSettings({...settings, hours_weekend_ar: e.target.value})} placeholder="الجمعة - السبت" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-right" dir="rtl" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><label className="block text-xs text-slate-600 mb-1">🇫🇷 Statut FR</label><input type="text" value={settings.hours_weekend_status_fr} onChange={(e) => setSettings({...settings, hours_weekend_status_fr: e.target.value})} placeholder="Fermé" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
              <div><label className="block text-xs text-slate-600 mb-1">🇬🇧 Statut EN</label><input type="text" value={settings.hours_weekend_status_en} onChange={(e) => setSettings({...settings, hours_weekend_status_en: e.target.value})} placeholder="Closed" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
              <div><label className="block text-xs text-slate-600 mb-1">🇩🇿 الحالة AR</label><input type="text" value={settings.hours_weekend_status_ar || ''} onChange={(e) => setSettings({...settings, hours_weekend_status_ar: e.target.value})} placeholder="مغلق" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-right" dir="rtl" /></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FooterManager;
